/**
 * Floor plan A4 세(portrait) PDF — `FloorPlanA4PreviewVertical` 본문 배치에 맞춤.
 */

import React from 'react';
import { Document, Page, View, StyleSheet, Image } from '@react-pdf/renderer';
import { PdfLayout } from '../../shared/enum/EnumRepository';
import { ProjectElementData } from '../../shared/dto/EntityRepository';
import { CvPdfText } from '../about/cvPdfShared';
import logoFit from '../../asset/gnb/logo_fit.png';
import type { FloorPlanCaptionLayout } from './floorPlanA4PreviewTypes';
import {
  buildCaptionSegments,
  floorPlanImageSource,
  FloorPlanPdfCaptions,
} from './documentFloorPlanPdfShared';

const FLOOR_PLAN_CAPTION_COLUMN_MAX_WIDTH_PT = 118;

/** A4 portrait 높이(pt) 대비 도면 영역 — `%` 높이는 react-pdf에서 레이아웃·border 해석 오류를 유발할 수 있어 고정 pt 사용 */
const A4_PORTRAIT_HEIGHT_PT = 842;
const FLOOR_PLAN_IMAGE_AREA_HEIGHT_PT = Math.round(A4_PORTRAIT_HEIGHT_PT * 0.35);

/** `fixed` 헤더·푸터와 겹치지 않도록 본문(도면+캡션) 상·하에 확보 */
const FLOOR_PLAN_FIXED_HEADER_RESERVE_PT = 78;
const FLOOR_PLAN_FIXED_FOOTER_RESERVE_PT = 34;

export interface DocumentFloorPlanVerticalProps {
  title: string;
  subtitle: string;
  date: string;
  gallery: string;
  location: string;
  /** 단일 페이지일 때만 쓰던 문자열; PDF 푸터는 `pageNumber / totalPages`로 고정 렌더 */
  pageNumberLabel: string;
  floorImageSrc: string;
  workElements: ProjectElementData[];
  captionLayout: FloorPlanCaptionLayout;
  includeArtist: boolean;
}

const DocumentFloorPlanVertical: React.FC<DocumentFloorPlanVerticalProps> = ({
  title,
  subtitle,
  date,
  gallery,
  location,
  pageNumberLabel: _pageNumberLabel,
  floorImageSrc,
  workElements,
  captionLayout,
  includeArtist,
}) => {
  const captionSegments = buildCaptionSegments(
    workElements,
    captionLayout,
    PdfLayout.Vertical,
    includeArtist,
  );

  const captionStyleRefs = {
    captionColumnsWrap: PDFStyles.Section.captionColumnsWrap,
    captionRowsWrap: PDFStyles.Section.captionRowsWrap,
    captionColumn: PDFStyles.Section.captionColumn,
    captionRowSingleLine: PDFStyles.Section.captionRowSingleLine,
    captionRowTextWrap: PDFStyles.Section.captionRowTextWrap,
    captionBlock: PDFStyles.Section.captionBlock,
    captionRight: PDFStyles.Section.captionRight,
    captionIndex: PDFStyles.Text.captionIndex,
    captionTitle: PDFStyles.Text.captionTitle,
    captionLine: PDFStyles.Text.captionLine,
    captionRowInline: PDFStyles.Text.captionRowInline,
  };

  return (
    <Document>
      <Page size="A4" style={PDFStyles.Page.root}>
        <View style={PDFStyles.Section.pageBody}>
          <View style={PDFStyles.Section.floorImageWrap}>
            <Image source={floorPlanImageSource(floorImageSrc)} style={PDFStyles.Image.floorPlan} />
          </View>

          <View
            style={
              captionLayout === 'row'
                ? PDFStyles.Section.captionPaneInnerRow
                : PDFStyles.Section.captionPaneInnerColumn
            }
          >
            <FloorPlanPdfCaptions
              captionSegments={captionSegments}
              captionLayout={captionLayout}
              includeArtist={includeArtist}
              s={captionStyleRefs}
            />
          </View>
        </View>

        <View style={PDFStyles.Section.fixedHeaderLeft} fixed>
          <CvPdfText style={PDFStyles.Text.titleLine}>{title}</CvPdfText>
          <CvPdfText
            style={PDFStyles.Text.metaLine}
            render={({ pageNumber }: { pageNumber: number }) => (pageNumber === 1 ? subtitle : '')}
          />
          <CvPdfText
            style={PDFStyles.Text.metaLine}
            render={({ pageNumber }: { pageNumber: number }) => (pageNumber === 1 ? date : '')}
          />
        </View>

        <View style={PDFStyles.Section.fixedLogoWrap} fixed>
          <Image source={logoFit} style={PDFStyles.Image.logo} />
        </View>

        <View style={PDFStyles.Section.fixedFooterRow} fixed>
          <View style={PDFStyles.Section.footerCell}>
            <CvPdfText style={PDFStyles.Text.footerLeft}>{gallery}</CvPdfText>
          </View>
          <View style={PDFStyles.Section.footerCellCenter}>
            <CvPdfText style={PDFStyles.Text.footerCenter}>{location}</CvPdfText>
          </View>
          <View style={PDFStyles.Section.footerCellRight}>
            <CvPdfText
              style={PDFStyles.Text.footerRight}
              render={({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) =>
                `${pageNumber} / ${totalPages}`
              }
            />
          </View>
        </View>
      </Page>
    </Document>
  );
};

const PDFTypography = {
  titleLine: { fontSize: 14, fontFamily: 'NotoKR', fontWeight: 600 },
  metaLine: { fontSize: 10, fontFamily: 'NotoKR', fontWeight: 400 },
  captionIndex: { fontSize: 7, fontFamily: 'NotoKR', fontWeight: 500, minWidth: 16 },
  captionTitle: { fontSize: 7, fontFamily: 'NotoKR', fontWeight: 500 },
  captionLine: { fontSize: 6.5, fontFamily: 'NotoKR', fontWeight: 400 },
  footer: { fontSize: 7, fontFamily: 'NotoKR', fontWeight: 400 },
};

const PDFStyles = {
  Page: StyleSheet.create({
    root: {
      paddingTop: 28,
      paddingBottom: 22,
      paddingLeft: 26,
      paddingRight: 26,
      backgroundColor: '#ffffff',
      fontFamily: 'NotoKR',
      fontWeight: 400,
      flexDirection: 'column',
    },
  }),

  Image: StyleSheet.create({
    logo: {
      width: 18,
      height: 18,
      objectFit: 'contain' as const,
    },
    floorPlan: {
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain' as const,
      objectPosition: 'left top' as const,
    },
  }),

  Section: StyleSheet.create({
    pageBody: {
      flex: 1,
      width: '100%',
      minHeight: 0,
      flexDirection: 'column',
      paddingTop: FLOOR_PLAN_FIXED_HEADER_RESERVE_PT,
      paddingBottom: FLOOR_PLAN_FIXED_FOOTER_RESERVE_PT,
    },
    fixedHeaderLeft: {
      position: 'absolute',
      top: 28,
      left: 26,
      maxWidth: '72%',
      flexDirection: 'column',
    },
    fixedLogoWrap: {
      position: 'absolute',
      top: 28,
      right: 26,
    },
    fixedFooterRow: {
      position: 'absolute',
      bottom: 22,
      left: 26,
      right: 26,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
    },
    floorImageWrap: {
      width: '100%',
      height: FLOOR_PLAN_IMAGE_AREA_HEIGHT_PT,
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      marginBottom: 12,
      flexDirection: 'column',
      flexShrink: 0,
    },
    /** Column — 본문(`pageBody`) 가로폭 대비 95% */
    captionPaneInnerColumn: {
      width: '95%',
      maxWidth: '95%',
      alignSelf: 'flex-start',
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      flexDirection: 'column',
    },
    /** Row — 가로폭 대비 80%, 왼쪽 여백 30pt */
    captionPaneInnerRow: {
      width: '80%',
      maxWidth: '80%',
      alignSelf: 'flex-start',
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      flexDirection: 'column',
    },
    captionColumnsWrap: {
      flexGrow: 1,
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      width: '100%',
      marginTop: 4,
      paddingTop: 6,
      paddingBottom: 8,
      minHeight: 0,
    },
    captionRowsWrap: {
      flexGrow: 1,
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      width: '100%',
      marginTop: 4,
      paddingTop: 6,
      paddingBottom: 8,
      minHeight: 0,
    },
    captionColumn: {
      width: FLOOR_PLAN_CAPTION_COLUMN_MAX_WIDTH_PT,
      maxWidth: FLOOR_PLAN_CAPTION_COLUMN_MAX_WIDTH_PT,
      flexGrow: 0,
      flexShrink: 0,
      flexDirection: 'column',
      alignItems: 'flex-start',
      paddingLeft: 10,
      paddingRight: 20,
    },
    captionRowSingleLine: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      width: '100%',
      minWidth: 0,
      marginBottom: 5,
    },
    /** Row 본문: 행 폭 대비 최대 70% — 길면 줄바꿈, 번호 블록(`captionIndex`)과 분리 */
    captionRowTextWrap: {
      width: '70%',
      maxWidth: '70%',
      minWidth: 0,
      flexShrink: 0,
      flexGrow: 0,
      flexDirection: 'column',
    },
    captionBlock: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      width: '100%',
      marginBottom: 10,
    },
    captionRight: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    footerCell: {
      flex: 1,
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    footerCellCenter: {
      flex: 1,
      flexDirection: 'column',
      alignItems: 'center',
      paddingLeft: 4,
      paddingRight: 4,
    },
    footerCellRight: {
      flex: 1,
      flexDirection: 'column',
      alignItems: 'flex-end',
    },
  }),

  Text: StyleSheet.create({
    titleLine: { ...PDFTypography.titleLine, marginBottom: 0 },
    metaLine: { ...PDFTypography.metaLine, marginBottom: 6 },
    captionIndex: {
      ...PDFTypography.captionIndex,
      marginRight: 8,
      /** Row 줄바꿈 시 본문이 번호 아래로 들어가지 않도록 */
      alignSelf: 'flex-start',
    },
    captionTitle: { ...PDFTypography.captionTitle, textAlign: 'left' as const, marginBottom: 1 },
    captionLine: { ...PDFTypography.captionLine, textAlign: 'left' as const },
    captionRowInline: {
      ...PDFTypography.captionLine,
      textAlign: 'left' as const,
      lineHeight: 1.35,
      width: '100%',
      maxWidth: '100%',
    },
    footerLeft: { ...PDFTypography.footer, textAlign: 'left' as const },
    footerCenter: { ...PDFTypography.footer, textAlign: 'center' as const },
    footerRight: { ...PDFTypography.footer, textAlign: 'right' as const },
  }),
};

export default DocumentFloorPlanVertical;
